import type { ReviewField } from '@hvac/contracts';

/**
 * The receipt's items, as the review screen shows them: in document order,
 * each line's fields together, and a check that the items add up to what the
 * receipt says. A misread item is the commonest misreading there is, and the
 * sum is how a reviewer spots one without reading every line twice.
 */

const ITEM = /^lineItems\[(\d+)\]\.(.+)$/;
const LEAF_ORDER = [
  'description',
  'quantity',
  'unitCost',
  'extendedCost',
  'equipmentBrand',
  'modelNumbers',
  'serialNumbers',
];

/** Sorts `lineItems[10]` after `lineItems[2]`, and each line's fields in reading order. */
export function compareItemPaths(a: string, b: string): number {
  const ma = ITEM.exec(a);
  const mb = ITEM.exec(b);
  if (ma === null || mb === null) return a.localeCompare(b);
  const byLine = Number(ma[1]) - Number(mb[1]);
  if (byLine !== 0) return byLine;
  const rank = (leaf: string | undefined) => {
    const index = LEAF_ORDER.indexOf(leaf ?? '');
    return index === -1 ? LEAF_ORDER.length : index;
  };
  return rank(ma[2]) - rank(mb[2]);
}

/** "$1,234.56", "1234.5", "-3.00" → cents. Null when it is not money. */
export function moneyToMinor(value: unknown): bigint | null {
  if (typeof value === 'number' && Number.isFinite(value)) return BigInt(Math.round(value * 100));
  if (typeof value !== 'string') return null;
  const text = value.replace(/[$,\s]/g, '');
  const match = /^(-)?(\d+)(?:\.(\d{1,2}))?$/.exec(text);
  if (match === null) return null;
  const cents = BigInt(match[2] ?? '0') * 100n + BigInt((match[3] ?? '').padEnd(2, '0'));
  return match[1] === '-' ? -cents : cents;
}

const valueOf = (field: ReviewField): unknown => field.draftValue ?? field.predictedValue;

export interface ItemsCheck {
  lineCount: number;
  itemsMinor: bigint;
  /** The figure the items should add to: the subtotal if printed, else the total. */
  againstMinor: bigint | null;
  againstLabel: 'subtotal' | 'total' | null;
  agrees: boolean | null;
}

/**
 * Whether the items add up. Items are usually before tax, so they are held
 * against the subtotal; a receipt with no subtotal (tax included in prices)
 * is held against the total. Two cents of rounding is allowed per receipt.
 */
export function checkItems(fields: readonly ReviewField[]): ItemsCheck {
  const amounts = fields
    .filter((field) => /^lineItems\[\d+\]\.extendedCost$/.test(field.fieldPath))
    .map((field) => moneyToMinor(valueOf(field)));
  const itemsMinor = amounts.reduce<bigint>((sum, amount) => sum + (amount ?? 0n), 0n);
  const find = (path: string) => {
    const field = fields.find((candidate) => candidate.fieldPath === path);
    return field === undefined ? null : moneyToMinor(valueOf(field));
  };
  const subtotal = find('totals.subtotal');
  const total = find('totals.total');
  const againstMinor = subtotal ?? total;
  const againstLabel = subtotal !== null ? 'subtotal' : total !== null ? 'total' : null;
  const diff = againstMinor === null ? null : itemsMinor - againstMinor;
  return {
    lineCount: amounts.length,
    itemsMinor,
    againstMinor,
    againstLabel,
    agrees:
      againstMinor === null || amounts.length === 0
        ? null
        : // Tax-included receipts: items may equal the total instead.
          (diff !== null && diff >= -2n && diff <= 2n) ||
          (total !== null && itemsMinor - total >= -2n && itemsMinor - total <= 2n),
  };
}
